import { useState } from "react";
import { useRouter } from "next/navigation";
import { Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { api } from "~/utils/api";
import { useUser } from "~/modules/user/hooks/useUser";
import { useToaster } from "~/modules/toaster/hooks/useToaster";
import { Routes } from "~/routes";
import { Chart } from "~/modules/charts/types";
import { Modal } from "~/components/Modal";
import { track } from "~/modules/analytics/track";
import { ChartsUserEvents } from "~/modules/analytics/events/charts";

// // // //

export function ChartDeleteButton({ chart }: { chart: Chart }) {
    const router = useRouter();
    const { user } = useUser();
    const { addToaster } = useToaster();
    const [open, setOpen] = useState(false);

    const deleteChart = api.charts.delete.useMutation({
        onSuccess: () => {
            addToaster({
                variant: "success",
                message: `Chart "${chart.title}" deleted.`,
            });
            router.push(Routes.charts);
        },
        onError: (error) => {
            addToaster({
                variant: "danger",
                message: error.message || "Error deleting chart.",
            });
        },
    });

    const isDeleting = deleteChart.isLoading;

    return (
        <>
            <Button
                variant="outline"
                onClick={() => setOpen(true)}
                aria-label="Delete chart"
                title="Delete"
                className="aspect-square px-0 text-muted-foreground hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
            >
                <Trash2 className="size-5" />
            </Button>

            <Modal
                show={open}
                onHide={() => {
                    if (!isDeleting) setOpen(false);
                }}
            >
                <div className="bg-card px-6 py-6 rounded-md">
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">
                        Delete chart
                    </h3>
                    <p className="text-sm text-slate-500 mb-6">
                        Are you sure you want to delete &ldquo;
                        {chart.title || chart.id}&rdquo;? This action cannot be
                        undone.
                    </p>
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setOpen(false)}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                // Delete the chart
                                deleteChart.mutate({
                                    id: chart.id,
                                    organizationID: user.organization.id,
                                });

                                // Track the event
                                track(
                                    ChartsUserEvents.ChartIndividualClickDelete,
                                    {
                                        chart_slug: chart.slug,
                                        chart_type: chart.type,
                                    },
                                );
                            }}
                            disabled={isDeleting}
                        >
                            {isDeleting ? "Deleting..." : "Delete"}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
